import { useEffect, useState } from "react";
import UserAvatar from "@/components/ui/UserAvatar";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { Users, Check, Clock, X, Eye, EyeOff, MessageSquare } from "lucide-react";
import { businessSecondsBetween } from "@/lib/businessHours";

const ROLE_LABEL = { executor: "Exécuteur", manager: "Manager", helper: "Aide exécuteur" };
const STATE = {
  accepted: { color: "#5fbf8f", Icon: Check, soft: "#3f8a66", light: "#a9e2c4", order: 2 },
  pending: { color: "#e0a94a", Icon: Clock, soft: "#a57c33", light: "#eecf95", order: 1 },
  refused: { color: "#d97a7a", Icon: X, soft: "#9c5656", light: "#efb3b3", order: 0 },
};
const WORK_DAY = 9 * 3600;
const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { timeZone: "Africa/Algiers", day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }).replace(",", " à");
/** iter437 pt 1.3 — miroir de services/business_hours.format_work_duration : « 45 min » · « 3 h 20 » · « 1 j 2 h ». */
export const fmtWork = (s) => { s = Math.max(0, Math.floor(s || 0)); const m = Math.floor(s / 60); if (m < 60) return `${m} min`; if (s < WORK_DAY) return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")}`; const d = Math.floor(s / WORK_DAY); return `${d} j ${Math.floor((s - d * WORK_DAY) / 3600)} h`; };
/** iter437 pt 1.1 — < 2 h gris · ≤ 1 j ouvré orange · au-delà rouge. */
export const workColor = (s) => (s < 2 * 3600 ? "#8A8A93" : s <= WORK_DAY ? "#E0822E" : "#E24B4A");
const seenLabel = (iso) => { const d = new Date(iso); const o = { timeZone: "Africa/Algiers" }; const today = new Date().toLocaleDateString("fr-FR", o) === d.toLocaleDateString("fr-FR", o); const hm = d.toLocaleTimeString("fr-FR", { ...o, hour: "2-digit", minute: "2-digit" }); return today ? `Vu à ${hm}` : `Vu le ${d.toLocaleDateString("fr-FR", { ...o, day: "2-digit", month: "2-digit" })} à ${hm}`; };

/** Indicateur Vu / Non vu (2 lignes, icône 18px sans fond). Le « Non vu » avance chaque minute en heures ouvrées (miroir frontend de la fonction backend). */
function SeenIndicator({ a, serverNow }) {
  const [tick, setTick] = useState(0);
  useEffect(() => { if (a.first_read_at) return undefined; const id = setInterval(() => setTick((t) => t + 1), 60000); return () => clearInterval(id); }, [a.first_read_at]);
  if (a.first_read_at) {
    return (
      <span className="flex items-center shrink-0" style={{ gap: 7 }} data-testid={`assignee-seen-${a.user_id}`} data-seen="1">
        <Eye size={18} style={{ color: "#4ADE80" }} />
        <span className="flex flex-col" style={{ lineHeight: 1.25 }}>
          <span style={{ fontSize: 11.5, color: "#4ADE80" }}>{seenLabel(a.first_read_at)}</span>
          <span style={{ fontSize: 10, color: "#8A8A93" }}>en {fmtWork(a.seen_work_seconds)}</span>
        </span>
      </span>
    );
  }
  const base = Number(a.unseen_work_seconds || 0);
  const elapsed = serverNow ? businessSecondsBetween(new Date(serverNow).getTime(), Date.now()) : 0;
  const s = base + (tick >= 0 ? elapsed : 0);
  const c = workColor(s);
  return (
    <span className="flex items-center shrink-0" style={{ gap: 7 }} data-testid={`assignee-seen-${a.user_id}`} data-seen="0" data-color={c}>
      <EyeOff size={18} style={{ color: c }} />
      <span className="flex flex-col" style={{ lineHeight: 1.25 }}>
        <span style={{ fontSize: 11.5, color: c }}>Non vu</span>
        <span className="inline-flex items-center" style={{ gap: 4, fontSize: 10, color: c }}><Clock size={10} />depuis {fmtWork(s)}</span>
      </span>
    </span>
  );
}

function AssigneeCard({ a, u, serverNow }) {
  const navigate = useNavigate();
  const st = STATE[a.accept_status] || STATE.pending;
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
  const comment = a.refusal_reason || a.completion_reason;
  const when = a.accept_status === "refused" ? ["Refusé", a.decided_at] : a.accept_status === "accepted" ? ["Accepté", a.decided_at || a.assigned_at] : ["Assigné", a.assigned_at];
  return (
    <div style={{ background: "#18181c", borderRadius: 8, overflow: "hidden", borderLeft: `3px solid ${st.color}` }} data-testid={`assignee-row-${a.user_id}`} data-state={a.accept_status}>
      <div className="flex items-center" style={{ gap: 10, padding: "10px 12px" }}>
        <button type="button" onClick={() => navigate(`/parametres?tab=users&highlight=${encodeURIComponent(a.user_id)}`)} className="relative shrink-0" style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }} title={`Ouvrir la fiche de ${name}`}>
          <UserAvatar user={u} size={34} showPopover={false} />
          <span className="absolute inline-flex items-center justify-center rounded-full" style={{ right: -2, bottom: -2, width: 13, height: 13, background: st.color, border: "2px solid #18181c" }} data-testid={`assignee-state-dot-${a.user_id}`}>
            <st.Icon size={7} strokeWidth={3.5} color="#101013" />
          </span>
        </button>
        <div className="flex-1 min-w-0">
          <div style={{ fontSize: 12.5, color: "#e8e8ec" }} data-testid={`assignee-name-${a.user_id}`}>{name}</div>
          <div style={{ fontSize: 9.5, color: "#e0a94a", marginTop: 2 }}>{ROLE_LABEL[a.role] || a.role}</div>
        </div>
        <SeenIndicator a={a} serverNow={serverNow} />
      </div>
      {comment && (
        <div className="flex" style={{ gap: 8, padding: "9px 12px", background: "#101013", borderTop: "0.5px solid #222226" }} data-testid={`assignee-comment-${a.user_id}`}>
          <MessageSquare size={12} style={{ color: st.soft, marginTop: 2, flexShrink: 0 }} />
          <div className="min-w-0">
            <div style={{ fontSize: 10.5, lineHeight: 1.5, color: st.light, whiteSpace: "pre-wrap" }}>{comment}</div>
            {when[1] && <div style={{ fontSize: 9, color: "#5a5a63", marginTop: 3 }}>{when[0]} le {fmt(when[1])}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/** iter437 pt 1 — bloc ASSIGNÉS : Vu / Non vu (heures ouvrées) à la place de « Acceptée » ; acceptation = coche verte sur l'avatar (inchangée). */
export default function AssigneesBlock({ assignments = [], usersById, serverNow }) {
  const rows = assignments.filter((a) => usersById[a.user_id]).sort((x, y) => (STATE[x.accept_status]?.order ?? 1) - (STATE[y.accept_status]?.order ?? 1));
  const total = rows.length, seen = rows.filter((a) => a.first_read_at).length;
  return (
    <div data-testid="td-assignees-card">
      <div className="flex items-center" style={{ gap: 6, marginBottom: 9 }}>
        <Users size={12} style={{ color: "#8b8b93" }} />
        <span style={{ flex: 1, fontSize: 10, color: "#8b8b93", letterSpacing: ".05em" }}>ASSIGNÉS</span>
        {total > 0 && <span style={{ fontSize: 10.5, borderRadius: 6, padding: "2px 7px", background: "#13261F", color: "#4ADE80" }} data-testid="td-assignees-summary">{seen} sur {total} vu{seen > 1 ? "s" : ""}</span>}
      </div>
      {total === 0 ? <p className="text-sm text-muted-foreground">Aucun assigné.</p> : (
        <div className="flex flex-col" style={{ gap: 7 }}>{rows.map((a) => <AssigneeCard key={a.id} a={a} u={usersById[a.user_id]} serverNow={serverNow} />)}</div>
      )}
    </div>
  );
}
